// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, first, map, Observable } from 'rxjs';
import { getAllFeatureDetailsForSelectedVersion } from '../../../../app/api/remote-api/.portal.generated/fn/feature-details/get-all-feature-details-for-selected-version';
import { getFeatureImages } from '../../../../app/api/remote-api/.portal.generated/fn/feature-details/get-feature-images';
import { getWhatsNewConfig } from '../../../../app/api/remote-api/.portal.generated/fn/feature-details/get-whats-new-config';
import { MapApiService } from '../../shared/services/map-api.service';

export interface SesoData {
  SesoReleasedVersion: string;
  SesoReleasedYear: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureDetailsService {
  private currentVersion = '';
  private currentYear = '';
  private readonly initializationComplete = new BehaviorSubject<boolean>(false);
  showFeatureDetailsModal = new BehaviorSubject<boolean>(false);

  featureListForSelectedVersion = this.mapApiService.mapEndpoint(
    getAllFeatureDetailsForSelectedVersion
  );
  featureImageList = this.mapApiService.mapEndpoint(getFeatureImages);
  whatsNewConfig = this.mapApiService.mapEndpoint(getWhatsNewConfig);

  constructor(private readonly mapApiService: MapApiService) {
    this.initializeConfig();
  }

  private initializeConfig(): void {
    this.whatsNewConfig.request().subscribe((data) => {
      if (data.sesoReleasedYear && data.sesoReleasedVersion) {
        this.currentVersion = data.sesoReleasedVersion;
        this.currentYear = data.sesoReleasedYear;
        const storedSesoData = this.readStoredSesoData();
        if (
          storedSesoData.SesoReleasedYear !== this.currentYear ||
          storedSesoData.SesoReleasedVersion !== this.currentVersion
        ) {
          this.saveSesoData({
            SesoReleasedVersion: this.currentVersion,
            SesoReleasedYear: this.currentYear,
          });
          this.showFeatureDetailsModal.next(true);
        }
      }
      this.initializationComplete.next(true);
    });
  }

  getStoredSesoData(): Observable<SesoData> {
    return this.initializationComplete.pipe(
      filter((initialized) => initialized), // Wait until initialization is complete
      first(), // Ensure it only resolves once
      map(() => this.readStoredSesoData())
    );
  }

  private readStoredSesoData(): SesoData {
    return JSON.parse(localStorage.getItem('SeSO-Data') ?? '{}');
  }

  private saveSesoData(data: SesoData): void {
    localStorage.setItem('SeSO-Data', JSON.stringify(data));
  }
}
